/**
 * Theme Management
 * Handles light/dark theme switching with localStorage persistence
 */

import { isNativePlatform } from './config';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme-preference';

let currentTheme: Theme = 'system';

/**
 * Get the user's saved theme preference
 */
export function getSavedTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark' || saved === 'system') {
    return saved;
  }
  return 'system';
}

/**
 * Get the effective theme (resolving 'system' to actual value)
 */
export function getEffectiveTheme(): 'light' | 'dark' {
  if (currentTheme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return currentTheme;
}

/**
 * Apply the theme to the document
 */
function applyTheme(theme: Theme): void {
  const html = document.documentElement;

  if (theme === 'system') {
    // Remove manual override, let CSS media query handle it
    html.removeAttribute('data-theme');
  } else {
    // Set manual override
    html.setAttribute('data-theme', theme);
  }

  // Update status bar on native platforms
  updateStatusBar(getEffectiveTheme());
}

/**
 * Update the native status bar style
 */
async function updateStatusBar(effectiveTheme: 'light' | 'dark'): Promise<void> {
  if (!isNativePlatform()) return;

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({
      style: effectiveTheme === 'dark' ? Style.Light : Style.Dark,
    });
    await StatusBar.setBackgroundColor({
      color: effectiveTheme === 'dark' ? '#0a0a0a' : '#f2f2f7',
    });
  } catch (err) {
    console.warn('Could not update status bar:', err);
  }
}

/**
 * Set the theme and persist the preference
 */
export function setTheme(theme: Theme): void {
  currentTheme = theme;
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

/**
 * Toggle between light and dark (skipping system)
 */
export function toggleTheme(): void {
  const effective = getEffectiveTheme();
  setTheme(effective === 'dark' ? 'light' : 'dark');
}

/**
 * Initialize theme system
 * Call this once on app startup
 */
export function initTheme(): void {
  currentTheme = getSavedTheme();
  applyTheme(currentTheme);

  // Listen for system theme changes
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    if (currentTheme === 'system') {
      // Re-apply to trigger status bar update
      applyTheme('system');
    }
  });
}

export default {
  getSavedTheme,
  getEffectiveTheme,
  setTheme,
  toggleTheme,
  initTheme,
};
